import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import useIsMobile from "@/lib/useIsMobile";
import { money } from "@/lib/ui";
import RodeoInfoDialog from "@/components/RodeoInfoDialog";
import AnnouncementBanner from "@/components/AnnouncementBanner";
import SamplerChecklist from "@/components/SamplerChecklist";
import { Loader2, Radio, Crosshair, Trophy, Clock, ChevronRight, ArrowUp, ArrowDown } from "lucide-react";

export default function Today() {
  const m = useIsMobile();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [info, setInfo] = useState(null);

  useEffect(() => { api.get("/today").then((r) => setData(r.data)).catch(() => setData({ running: [], closings: [], leaders: [], tightest: null })); }, []);

  const page = m ? "bg-[#0C1017] min-h-screen text-slate-200 pb-24" : "";
  const title = m ? "text-slate-100" : "text-[#14181F]";
  const card = m ? "bg-white/5 border-white/10" : "bg-white border-[#E4E7EB]";
  const sub = m ? "text-slate-400" : "text-slate-500";
  const niceDate = data?.date ? new Date(data.date + "T00:00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" }) : "";

  const RodeoRow = (r) => (
    <button key={r.id} onClick={() => (r.cfr || r.detail_url) ? nav(r.detail_url || "/cfr") : setInfo({ id: r.id, name: r.name })} data-testid={`today-rodeo-${r.id}`}
      className={`w-full text-left flex items-center gap-3 px-3 py-2.5 border-t transition-colors ${m ? "border-white/5 active:bg-white/5" : "border-[#EEF0F2] hover:bg-[#FAFBFC]"}`}>
      <div className="flex-1 min-w-0">
        <div className={`font-semibold text-sm truncate ${title} flex items-center gap-1.5`}>
          {r.cfr ? <Trophy className="w-3.5 h-3.5 text-[#E8B23A] shrink-0" /> : null}{r.name}
        </div>
        <div className={`text-[11px] truncate ${sub}`}>
          {[r.city, r.state].filter(Boolean).join(", ")}
          {r.associations?.length ? <span> · {r.associations.join(", ")}</span> : null}
          {r.added_money ? <span className="text-[#E68A2E]"> · {money(r.added_money)}</span> : null}
        </div>
      </div>
      <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />
    </button>
  );

  const SectionHead = ({ icon: Icon, children, badge }) => (
    <div className="flex items-center gap-2 mb-2">
      <Icon className="w-4 h-4 text-[#C8000F]" />
      <h2 className={`font-display font-bold text-sm uppercase tracking-wide ${title}`}>{children}</h2>
      {badge != null && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-[#C8000F] text-white">{badge}</span>}
    </div>
  );

  return (
    <div className={page} data-testid="today-page">
      <div className={`px-4 sm:px-6 py-5 border-b ${m ? "border-white/10" : "border-[#E4E7EB]"}`}>
        <div className="flex items-center gap-2">
          <span className="relative flex h-2.5 w-2.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#C8000F] opacity-75" /><span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#C8000F]" /></span>
          <h1 className={`font-display text-2xl font-extrabold tracking-tight ${title}`}>Rodeo, right now</h1>
        </div>
        <p className={`text-sm mt-1 ${sub}`}>{niceDate}</p>
      </div>

      <div className="p-4 sm:p-6 max-w-4xl space-y-6">
        <AnnouncementBanner m={m} />
        <SamplerChecklist m={m} />
        {data === null ? (
          <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#C8000F]" /></div>
        ) : (
          <>
            {/* The bubble */}
            {data.tightest && (
              <section>
                <SectionHead icon={Crosshair}>The bubble · tightest race</SectionHead>
                <button onClick={() => nav("/nfr-cutline")} data-testid="today-bubble"
                  className={`w-full text-left rounded-xl border p-4 ${card} ring-1 ring-[#C8000F]/30`}>
                  <div className="flex items-center justify-between">
                    <div className={`font-display font-bold ${title}`}>{data.tightest.event}</div>
                    <div className="text-[#C8000F] font-bold text-sm">{money(data.tightest.gap)} <span className={`font-semibold ${sub}`}>apart</span></div>
                  </div>
                  <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
                    <div className={`rounded-lg px-3 py-2 ${m ? "bg-[#16A34A]/10" : "bg-[#EAF3EC]"}`}>
                      <div className="flex items-center gap-1 text-[10px] font-bold uppercase text-[#166534]"><ArrowUp className="w-3 h-3" />#15 in</div>
                      <div className={`font-semibold truncate ${title}`}>{data.tightest.in.name}</div>
                      <div className={sub}>{money(data.tightest.in.earnings)}</div>
                    </div>
                    <div className={`rounded-lg px-3 py-2 ${m ? "bg-white/5" : "bg-[#F7F8FA]"}`}>
                      <div className={`flex items-center gap-1 text-[10px] font-bold uppercase ${sub}`}><ArrowDown className="w-3 h-3" />#16 out</div>
                      <div className={`font-semibold truncate ${sub}`}>{data.tightest.out.name}</div>
                      <div className={sub}>{money(data.tightest.out.earnings)}</div>
                    </div>
                  </div>
                  <div className="mt-2 text-[11px] font-semibold text-[#C8000F]">See the full cutline →</div>
                </button>
              </section>
            )}

            {/* Running today */}
            <section>
              <SectionHead icon={Radio} badge={data.running.length}>Running today</SectionHead>
              <div className={`rounded-xl border overflow-hidden ${card}`}>
                {data.running.length === 0 ? <div className={`px-3 py-8 text-center text-sm ${sub}`}>No rodeos running today.</div>
                  : data.running.map(RodeoRow)}
              </div>
            </section>

            {/* Closing today */}
            {data.closings.length > 0 && (
              <section>
                <SectionHead icon={Clock} badge={data.closings.length}>Entries closing today</SectionHead>
                <div className={`rounded-xl border overflow-hidden ${card}`}>{data.closings.map(RodeoRow)}</div>
              </section>
            )}

            {/* PRCA leaders */}
            {data.leaders.length > 0 && (
              <section>
                <SectionHead icon={Trophy}>PRCA world leaders</SectionHead>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {data.leaders.map((l) => (
                    <div key={l.abbr} data-testid={`today-leader-${l.abbr}`} className={`rounded-xl border p-3 flex items-center justify-between ${card}`}>
                      <div className="min-w-0">
                        <div className={`text-[10px] font-bold uppercase tracking-wide ${sub}`}>{l.event}</div>
                        <div className={`font-semibold text-sm truncate ${title}`}>{l.name}</div>
                      </div>
                      <div className="text-[#3B6B1E] font-bold text-sm shrink-0">{money(l.earnings)}</div>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </div>

      {info && <RodeoInfoDialog data={info} onClose={() => setInfo(null)} />}
    </div>
  );
}
